import { z } from 'zod';

/** Steam is the v1 operational surface. Enum mirrors the existing
 *  machine-credentials platform set so other launchers slot in later
 *  without a model redesign. */
export const launcherPlatformSchema = z.enum([
  'steam', 'epic', 'xbox', 'playstation', 'gog',
  'origin', 'ubisoft', 'battlenet', 'riot', 'rockstar',
]);
export type LauncherPlatform = z.infer<typeof launcherPlatformSchema>;

export const launcherAccountSchema = z.object({
  accountId: z.string(),
  platform: launcherPlatformSchema,
  username: z.string().min(1),
  /** Sensitive. Stripped from API reads unless caller holds
   *  VIEW_LAUNCHER_ACCOUNT_SECRET. Never log. */
  password: z.string().min(1),
  friendCode: z.string().optional(),
  region: z.string().optional(),
  vpnLocation: z.string().optional(),
  status: z.enum(['active', 'disabled']).default('active'),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type LauncherAccount = z.infer<typeof launcherAccountSchema>;

export const accountEntitlementSchema = z.object({
  entitlementId: z.string(),
  accountId: z.string(),
  platform: launcherPlatformSchema,
  /** Steam appid for v1 (e.g. '742300'). */
  gameId: z.string().min(1),
  title: z.string().min(1),
  /** Provenance only — we never store redeemable key strings. */
  source: z.enum(['key', 'purchase']).default('key'),
  createdAt: z.coerce.date(),
});
export type AccountEntitlement = z.infer<typeof accountEntitlementSchema>;

export const steamFamilySchema = z.object({
  familyId: z.string(),
  name: z.string().min(1), // e.g. 'Chtulu-Pod-00'
  region: z.string().optional(),
  createdAt: z.coerce.date(),
});
export type SteamFamily = z.infer<typeof steamFamilySchema>;

export const steamFamilyMembershipSchema = z.object({
  membershipId: z.string(),
  familyId: z.string(),
  accountId: z.string(),
  role: z.enum(['parent', 'member']),
  /** 'invited' covers the ops spreadsheet's "Friend of Parent" pre-join state. */
  state: z.enum(['invited', 'active']),
  /** Ops rule: family members must be added from the same machine + network
   *  as the family parent. Record which machine was used so the procedure
   *  is auditable. Optional — manual ops flow records it when known. */
  joinedFromMachineId: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type SteamFamilyMembership = z.infer<typeof steamFamilyMembershipSchema>;

export const accountAssignmentSchema = z.object({
  assignmentId: z.string(),
  accountId: z.string(),
  machineId: z.string(),
  /** Denormalized so the one-primary-account-per-(machine, platform)
   *  invariant is enforceable at the index level. */
  platform: launcherPlatformSchema,
  assignedBy: z.object({ userId: z.string(), email: z.string() }),
  assignedAt: z.coerce.date(),
  /** null = active assignment. History rows are never deleted. */
  unassignedBy: z.object({ userId: z.string(), email: z.string() }).nullable().default(null),
  unassignedAt: z.coerce.date().nullable().default(null),
});
export type AccountAssignment = z.infer<typeof accountAssignmentSchema>;

export const machinePackageSchema = z.object({
  machineId: z.string(),
  platform: launcherPlatformSchema,
  gameId: z.string().min(1),
  provisionedAt: z.coerce.date(),
  /** 'deployment' = written by the provisioning recorder;
   *  'manual' = emergency ops override (Phase 4 mark/unmark routes). */
  origin: z.enum(['deployment', 'manual']).default('deployment'),
  /** Deployment provenance — present when origin === 'deployment'. */
  sourceDeploymentId: z.string().optional(),
  sourceExecutionId: z.string().optional(),
  /** Manual provenance — present when origin === 'manual'. */
  markedBy: z.object({ userId: z.string(), email: z.string() }).optional(),
});
export type MachinePackage = z.infer<typeof machinePackageSchema>;

// ---- Computed / API shapes ----

export const familySlotHolderSchema = z.object({
  accountId: z.string(),
  machineId: z.string(),
  campaignId: z.string(),
  since: z.coerce.date(),
});
export type FamilySlotHolder = z.infer<typeof familySlotHolderSchema>;

export const familySlotStatusSchema = z.object({
  familyId: z.string(),
  gameId: z.string(),
  /** Entitlements for gameId across active family members (multiple keys
   *  for the same title = multiple slots). */
  totalSlots: z.number().int().nonnegative(),
  inUse: z.number().int().nonnegative(),
  holders: z.array(familySlotHolderSchema),
});
export type FamilySlotStatus = z.infer<typeof familySlotStatusSchema>;

/** Readiness check kinds (semantics corrected for #14652 — steam-accurate
 *  per-copy pool: every active play consumes one slot, owner's play
 *  included; see docs/feature-plans/steam-slot-accuracy/PHASE_1.md):
 *  - `account_assigned`: machine has an active steam account assignment.
 *  - `entitlement_available`: a license path exists — the account OWNS the
 *    title OR has an ACTIVE membership in a family with totalSlots > 0 for
 *    it (no longer "owns" only).
 *  - `package_provisioned`: the steam package is recorded on the machine.
 *  - `family_slot_available`: a slot is free for this machine now, excluding
 *    its own hold; trivially true for an owner with no active family
 *    membership (own copy, no pool). */
export const readinessCheckKindSchema = z.enum([
  'account_assigned',
  'entitlement_available',
  'package_provisioned',
  'family_slot_available',
]);
export type ReadinessCheckKind = z.infer<typeof readinessCheckKindSchema>;

export const campaignReadinessSchema = z.object({
  machineId: z.string(),
  campaignId: z.string(),
  /** eligible = account_assigned && package_provisioned &&
   *  entitlement_available && family_slot_available.
   *  (AND semantics since #14652: entitlement_available means "a license
   *  path exists", so a borrower ALSO needs family_slot_available to be
   *  sure a slot is free right now.) */
  eligible: z.boolean(),
  checks: z.array(z.object({
    kind: readinessCheckKindSchema,
    ok: z.boolean(),
    /** Human-readable reason. For `family_slot_available` failures the
     *  detail names the machine(s) currently holding the slot(s), so ops
     *  can trace the block without navigating away. */
    detail: z.string().optional(),
  })),
});
export type CampaignReadiness = z.infer<typeof campaignReadinessSchema>;

/** One row of the ops account spreadsheet (CSV import). Columns map 1:1 to
 *  the current export: Platform, Username, Password, Friend Code,
 *  Steam Family, In Family?, Friend of Parent, Family Parent,
 *  Assigned Machine, VPN Location, Region, Games-1..20.
 *  The "Current Game" / "Game Total" / "Game Catalogue" columns are
 *  intentionally NOT represented: current-game is runtime state owned by
 *  the lock service; total/catalogue are derivable from games[]. */
export const opsAccountImportRowSchema = z.object({
  platform: launcherPlatformSchema,
  username: z.string().min(1),
  password: z.string().min(1),
  friendCode: z.string().optional(),
  steamFamily: z.string().optional(),
  inFamily: z.boolean().default(false),
  friendOfParent: z.boolean().default(false),
  familyParent: z.boolean().default(false),
  assignedMachineId: z.string().optional(), // pcmid_...
  vpnLocation: z.string().optional(),
  region: z.string().optional(),
  games: z.array(z.object({ gameId: z.string(), title: z.string() })).default([]),
});
export type OpsAccountImportRow = z.infer<typeof opsAccountImportRowSchema>;
